import React, { useEffect, useMemo, useState } from 'react'
import { Alert, Box, Button, Grid, Stack, TextField, Typography } from '@mui/material'
import { QRCodeCanvas } from 'qrcode.react'
import MerchantToolShell, { shellStyles } from './MerchantToolShell'
import { useAuth } from '../../contexts/AuthContext'
import { useLanguage } from '../../contexts/LanguageContext'
import api from '../../services/api'

const keypadValues = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '.', '0', 'backspace']

export default function MerchantPosMode() {
    const { user } = useAuth()
    const { lang } = useLanguage()
    const isFr = lang === 'fr'
    const [amount, setAmount] = useState('')
    const [note, setNote] = useState('')
    const [presentedRequest, setPresentedRequest] = useState(null)
    const [recentRequests, setRecentRequests] = useState([])
    const [isSaving, setIsSaving] = useState(false)
    const [error, setError] = useState('')
    const merchantCountry = useMemo(
        () => String(user?.business_country || user?.businessCountry || user?.country || 'US').toUpperCase(),
        [user]
    )

    const parsedAmount = useMemo(() => Number(amount || 0), [amount])
    const merchantCurrency = useMemo(() => {
        return user?.default_currency || user?.business_currency || user?.baseCurrency || ({
            US: 'USD',
            USA: 'USD',
            CA: 'CAD',
            GB: 'GBP',
            UK: 'GBP',
            AU: 'AUD',
            FR: 'EUR',
            SL: 'SLE',
            LR: 'LRD',
            GN: 'GNF',
            GM: 'GMD',
        }[merchantCountry] || 'USD')
    }, [merchantCountry, user])

    const qrPayload = useMemo(() => {
        if (!(parsedAmount > 0)) return ''
        return JSON.stringify({
            type: 'mojopayr_pos_payment',
            amount: parsedAmount,
            currency: merchantCurrency,
            merchant_country: merchantCountry,
            merchant_currency: merchantCurrency,
            settlement_currency: merchantCurrency,
            merchant: user?.business_name || user?.businessName || user?.name || 'MojoPayr Merchant',
            note: note.trim(),
            created_at: new Date().toISOString(),
        })
    }, [merchantCountry, merchantCurrency, note, parsedAmount, user])

    const payableQrPayload = useMemo(() => {
        if (!presentedRequest?.reference) return qrPayload
        return JSON.stringify({
            type: 'mojopayr_pos_payment',
            reference: presentedRequest.reference,
            amount: Number(presentedRequest.amount || parsedAmount || 0),
            fee: Number(presentedRequest.fee || 0),
            total: Number(presentedRequest.total || presentedRequest.amount || parsedAmount || 0),
            currency: presentedRequest.currency || merchantCurrency,
            merchant_country: presentedRequest.merchant_country_code || presentedRequest.merchant_country || merchantCountry,
            merchant_currency: presentedRequest.merchant_currency || presentedRequest.currency || merchantCurrency,
            settlement_currency: presentedRequest.settlement_currency || presentedRequest.currency || merchantCurrency,
            merchant: user?.business_name || user?.businessName || user?.name || 'MojoPayr Merchant',
        })
    }, [merchantCountry, merchantCurrency, parsedAmount, presentedRequest, qrPayload, user])

    useEffect(() => {
        let active = true
        api.get('/merchant/pos-sales')
            .then((response) => {
                if (!active) return
                setRecentRequests(Array.isArray(response.data?.data) ? response.data.data : [])
            })
            .catch(() => {
                if (active) setRecentRequests([])
            })
        return () => {
            active = false
        }
    }, [])

    const handleKeypadPress = (value) => {
        setError('')
        if (value === 'backspace') {
            setAmount((current) => current.slice(0, -1))
            return
        }

        setAmount((current) => {
            if (value === '.' && current.includes('.')) return current
            if (value === '.' && current.length === 0) return '0.'
            return `${current}${value}`
        })
    }

    const handlePresentPayment = async () => {
        if (!(parsedAmount > 0)) {
            setError(isFr ? 'Ajoutez un montant local valide.' : 'Add a valid local amount.')
            return
        }

        setIsSaving(true)
        try {
            const response = await api.post('/merchant/pos-sales', {
                amount: parsedAmount,
                description: note.trim(),
                qr_payload: qrPayload,
            })
            const sale = response.data?.sale
            setPresentedRequest(sale)
            setRecentRequests((current) => [sale, ...current.filter((item) => item.reference !== sale?.reference)].slice(0, 10))
            setError('')
        } catch (err) {
            setError(err?.response?.data?.message || (isFr ? 'Impossible enregistrer la vente POS.' : 'Could not record the POS sale.'))
        } finally {
            setIsSaving(false)
        }
    }

    const handleCopyReference = async () => {
        if (!presentedRequest?.reference) return
        try {
            await navigator.clipboard.writeText(presentedRequest.reference)
        } catch {
            // no-op fallback for older browsers
        }
    }

    return (
        <MerchantToolShell
            eyebrow={isFr ? 'Paiement local' : 'Local acceptance'}
            title="POS Mode"
            subtitle={
                isFr
                    ? 'Saisissez le montant comme sur mobile, ajoutez une note de caisse si besoin, puis presentez le paiement au client.'
                    : 'Enter the amount like the mobile POS flow, add an optional checkout note, then present the payment to the customer.'
            }
            chips={[
                isFr ? 'Faible data' : 'Low data',
                isFr ? 'Clavier rapide' : 'Fast keypad',
                isFr ? 'Preuve prete' : 'Proof ready',
            ]}
            primaryAction={{
                label: isFr ? 'Enregistrer vente POS' : 'Record POS Sale',
                onClick: handlePresentPayment,
            }}
            left={
                <Stack spacing={2.2}>
                    {error && <Alert severity="error">{error}</Alert>}
                    {presentedRequest && (
                        <Alert severity="success">
                            {isFr
                                ? `Paiement cree. Reference ${presentedRequest.reference}. Total ${presentedRequest.total.toFixed(2)}.`
                                : `Payment created. Reference ${presentedRequest.reference}. Total ${presentedRequest.total.toFixed(2)}.`}
                        </Alert>
                    )}

                    <Typography
                        sx={{
                            textAlign: 'center',
                            fontWeight: 900,
                            color: '#FF8A00',
                            fontSize: { xs: 34, md: 42 },
                        }}
                    >
                        {amount || '0'}
                    </Typography>

                    {presentedRequest?.reference && payableQrPayload && (
                        <Box sx={{ display: 'grid', placeItems: 'center' }}>
                            <Box sx={{ bgcolor: '#FFFFFF', borderRadius: 4, p: 2, display: 'inline-flex' }}>
                                <QRCodeCanvas value={payableQrPayload} size={220} includeMargin />
                            </Box>
                            <Typography sx={{ color: 'text.secondary', fontSize: 13, mt: 1 }}>
                                {isFr
                                    ? `Scannez ou saisissez ${presentedRequest.reference}.`
                                    : `Scan or enter ${presentedRequest.reference}.`}
                            </Typography>
                        </Box>
                    )}

                    <TextField
                        label={isFr ? 'Description (facultative)' : 'Description (optional)'}
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder={isFr ? 'Ex: boissons, table 4' : 'Ex: drinks, table 4'}
                    />

                    <Grid container spacing={1.6}>
                        {keypadValues.map((value) => {
                            const isDelete = value === 'backspace'
                            return (
                                <Grid item xs={4} key={value}>
                                    <Button
                                        fullWidth
                                        onClick={() => handleKeypadPress(value)}
                                        sx={{
                                            minHeight: 112,
                                            borderRadius: 4,
                                            textTransform: 'none',
                                            fontSize: 28,
                                            fontWeight: 500,
                                            color: '#FFFFFF',
                                            bgcolor: isDelete ? '#FF555A' : 'rgba(91,46,255,0.10)',
                                            border: isDelete
                                                ? '1px solid rgba(255,85,90,0.55)'
                                                : '1px solid rgba(183,166,255,0.16)',
                                            '&:hover': {
                                                bgcolor: isDelete ? '#FF6469' : 'rgba(91,46,255,0.18)',
                                            },
                                        }}
                                    >
                                        {isDelete ? '⌫' : value}
                                    </Button>
                                </Grid>
                            )
                        })}
                    </Grid>
                    <Button
                        fullWidth
                        variant="contained"
                        disabled={isSaving || !(parsedAmount > 0)}
                        onClick={handlePresentPayment}
                        sx={{
                            bgcolor: '#FF8A00',
                            color: '#0B0620',
                            borderRadius: 999,
                            py: 1.35,
                            fontWeight: 900,
                            textTransform: 'none',
                            '&:hover': { bgcolor: '#FF9F2A' },
                        }}
                    >
                        {isSaving
                            ? (isFr ? 'Enregistrement...' : 'Recording...')
                            : (isFr ? 'Enregistrer vente POS' : 'Record POS Sale')}
                    </Button>
                </Stack>
            }
            right={
                <Stack spacing={2}>
                    <Box sx={shellStyles.softPanel}>
                        <Typography sx={{ fontWeight: 800, mb: 0.8 }}>
                            {isFr ? 'Paiement presente' : 'Presented payment'}
                        </Typography>
                        <Typography sx={{ color: 'text.secondary', fontSize: 13, mb: 1.2 }}>
                            {isFr
                                ? 'Le client suit ensuite le flux MojoPayr et la preuve revient dans les transactions.'
                                : 'The customer then follows the MojoPayr payment flow and the proof returns to transactions.'}
                        </Typography>
                        <Typography sx={{ fontWeight: 900, fontSize: 30 }}>
                            {merchantCurrency} {Number.isFinite(parsedAmount) ? parsedAmount.toFixed(2) : '0.00'}
                        </Typography>
                        <Typography sx={{ color: 'text.secondary', mt: 1, fontSize: 13 }}>
                            {note || (isFr ? 'Ajoutez une note facultative pour la caisse.' : 'Add an optional checkout note.')}
                        </Typography>
                    </Box>

                    <Box sx={shellStyles.softPanel}>
                        <Typography sx={{ fontWeight: 800, mb: 1.2 }}>
                            {isFr ? 'Paiements POS recents' : 'Recent POS payments'}
                        </Typography>
                        {presentedRequest ? (
                            <Stack spacing={0.8}>
                                <Typography sx={{ fontWeight: 800 }}>{presentedRequest.reference}</Typography>
                                <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
                                    {isFr
                                        ? `Montant ${presentedRequest.currency} ${Number(presentedRequest.amount).toFixed(2)}`
                                        : `Amount ${presentedRequest.currency} ${Number(presentedRequest.amount).toFixed(2)}`}
                                </Typography>
                                <Button
                                    variant="outlined"
                                    onClick={handleCopyReference}
                                    sx={{
                                        alignSelf: 'flex-start',
                                        color: '#FFFFFF',
                                        borderColor: 'rgba(183,166,255,0.34)',
                                        borderRadius: 4,
                                        textTransform: 'none',
                                        fontWeight: 800,
                                        mt: 0.5,
                                    }}
                                >
                                    {isFr ? 'Copier reference' : 'Copy Reference'}
                                </Button>
                            </Stack>
                        ) : recentRequests.length > 0 ? (
                            <Stack spacing={1}>
                                {recentRequests.map((request) => (
                                    <Box key={request.id} sx={{ ...shellStyles.softPanel, p: 1.5 }}>
                                        <Typography sx={{ fontWeight: 800 }}>{request.reference}</Typography>
                                        <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
                                            {isFr
                                                ? `Montant ${request.currency} ${Number(request.amount).toFixed(2)}`
                                                : `Amount ${request.currency} ${Number(request.amount).toFixed(2)}`}
                                        </Typography>
                                    </Box>
                                ))}
                            </Stack>
                        ) : (
                            <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
                                {isFr
                                    ? 'Aucune presentation POS creee pour le moment.'
                                    : 'No POS payment request created yet.'}
                            </Typography>
                        )}
                    </Box>

                    {payableQrPayload && (
                        <Box sx={{ ...shellStyles.softPanel, display: 'grid', placeItems: 'center' }}>
                            <Typography sx={{ fontWeight: 800, justifySelf: 'start', mb: 1.2 }}>
                                {isFr ? 'Code QR de paiement' : 'Payment QR'}
                            </Typography>
                            <Box sx={{ bgcolor: '#FFFFFF', borderRadius: 4, p: 2, display: 'inline-flex' }}>
                                <QRCodeCanvas value={payableQrPayload} size={240} includeMargin />
                            </Box>
                            {presentedRequest?.reference && (
                                <Typography sx={{ color: 'text.secondary', fontSize: 13, mt: 1.2, textAlign: 'center' }}>
                                    {isFr
                                        ? `Scannez ou saisissez ${presentedRequest.reference}.`
                                        : `Scan or enter ${presentedRequest.reference}.`}
                                </Typography>
                            )}
                        </Box>
                    )}
                </Stack>
            }
        />
    )
}
